export interface DevOpsKpi {
  labelKey: string;
  value: string;
  suffix: string;
  icon: string;
  iconColorVar: string;
  sparkline: number[];
}

export type DeploymentStageStatus = 'deployed' | 'deploying' | 'failed' | 'pending';

export interface DeploymentStage {
  name: string;
  version: string;
  when: string;
  status: DeploymentStageStatus;
}

export interface DeploymentService {
  service: string;
  branch: string;
  stages: DeploymentStage[];
}

export type ServiceHealthStatus = 'healthy' | 'degraded';

export interface ServiceHealth {
  label: string;
  sub: string;
  uptime: number;
}

export const HEALTHY_UPTIME_THRESHOLD = 99.9;

export function serviceHealthStatus(uptime: number): ServiceHealthStatus {
  return uptime >= HEALTHY_UPTIME_THRESHOLD ? 'healthy' : 'degraded';
}

export const DEVOPS_KPIS: readonly DevOpsKpi[] = [
  {
    labelKey: 'liveDashboard.devops.kpis.deployments',
    value: '32',
    suffix: '',
    icon: 'bar_chart',
    iconColorVar: '--to-chart-running',
    sparkline: [3, 5, 4, 6, 5, 4, 5],
  },
  {
    labelKey: 'liveDashboard.devops.kpis.pipelineSuccess',
    value: '94.2',
    suffix: '%',
    icon: 'check',
    iconColorVar: '--to-chart-passed',
    sparkline: [90, 91, 89, 92, 93, 92, 94],
  },
  {
    labelKey: 'liveDashboard.devops.kpis.mttr',
    value: '14',
    suffix: 'min',
    icon: 'settings',
    iconColorVar: '--to-info-dot',
    sparkline: [22, 19, 18, 17, 15, 16, 14],
  },
  {
    labelKey: 'liveDashboard.devops.kpis.openIncidents',
    value: '2',
    suffix: '',
    icon: 'shield',
    iconColorVar: '--to-warn-dot',
    sparkline: [5, 4, 4, 3, 3, 3, 2],
  },
];

export const DEPLOYMENT_SERVICES: readonly DeploymentService[] = [
  {
    service: 'testOFFICE Web',
    branch: 'main',
    stages: [
      { name: 'Staging', version: 'v2.14.0', when: 'vor 12 Min.', status: 'deployed' },
      { name: 'Test', version: 'v2.14.0', when: 'läuft jetzt…', status: 'deploying' },
      { name: 'Production', version: 'v2.13.4', when: 'vor 1 Tag', status: 'pending' },
    ],
  },
  {
    service: 'testOFFICE API',
    branch: 'release/2026.05',
    stages: [
      { name: 'Staging', version: 'v1.8.2', when: 'vor 1 Std.', status: 'deployed' },
      { name: 'Test', version: 'v1.8.2', when: 'vor 32 Min.', status: 'deployed' },
      { name: 'Production', version: 'v1.8.1', when: 'vor 3 Tagen', status: 'deployed' },
    ],
  },
  {
    service: 'Auth-Service',
    branch: 'fix/sso-edge',
    stages: [
      { name: 'Staging', version: 'v0.9.7', when: 'vor 4 Std.', status: 'failed' },
      { name: 'Test', version: 'v0.9.6', when: 'vor 1 Tag', status: 'deployed' },
      { name: 'Production', version: 'v0.9.6', when: 'vor 2 T.', status: 'deployed' },
    ],
  },
];

export const SERVICE_HEALTH: readonly ServiceHealth[] = [
  { label: 'testOFFICE Web', sub: 'https://app.testoffice.com', uptime: 99.98 },
  { label: 'testOFFICE API', sub: 'https://api.testoffice.com', uptime: 99.94 },
  { label: 'Auth-Service', sub: 'auth.testoffice.internal', uptime: 99.7 },
  { label: 'Storage', sub: 's3.testoffice.internal', uptime: 100 },
  { label: 'Background-Jobs', sub: 'workers · 4 Replicas', uptime: 99.2 },
];

export type CiBuildStatus = 'passed' | 'failed' | 'running';

export interface DevOpsCiBuild {
  id: string;
  commit: string;
  author: string;
  status: CiBuildStatus;
  dauer: string;
}

export type IncidentStatus = 'open' | 'resolved';

export interface DevOpsIncident {
  id: string;
  titel: string;
  desc: string;
  status: IncidentStatus;
  icon: string;
  time: string;
  owner: string;
}

export const DEVOPS_CI_BUILDS: readonly DevOpsCiBuild[] = [
  {
    id: '#12384',
    commit: 'feat: SSO refresh-token rotation',
    author: 'Sebastian Grätz',
    status: 'passed',
    dauer: '4 m 12 s',
  },
  {
    id: '#12383',
    commit: 'fix: PDF-Export bei Umlauten',
    author: 'Toni Gansel',
    status: 'passed',
    dauer: '3 m 58 s',
  },
  {
    id: '#12382',
    commit: 'chore: bump dependencies',
    author: 'Marc Zemelka',
    status: 'failed',
    dauer: '2 m 04 s',
  },
  {
    id: '#12381',
    commit: 'feat: Refined Dashboard-Kacheln',
    author: 'Carina Ernst',
    status: 'running',
    dauer: 'läuft…',
  },
  {
    id: '#12380',
    commit: 'test: NEX-11 Akzeptanzkriterien',
    author: 'Felicitas F.',
    status: 'passed',
    dauer: '5 m 22 s',
  },
];

export const DEVOPS_INCIDENTS: readonly DevOpsIncident[] = [
  {
    id: 'INC-014',
    titel: 'Auth-Service · Erhöhte Latenz',
    desc: 'p95 > 1.2 s seit 14:02 · Untersuchung läuft',
    status: 'open',
    icon: 'shield',
    time: 'vor 28 Min.',
    owner: 'Sebastian Grätz',
  },
  {
    id: 'INC-013',
    titel: 'PDF-Generator Memory-Spike',
    desc: 'Worker-Restart benötigt · Mitigation deployed',
    status: 'resolved',
    icon: 'check',
    time: 'vor 2 Std.',
    owner: 'Marc Zemelka',
  },
  {
    id: 'INC-012',
    titel: 'Storage-Sync verzögert',
    desc: 'S3-Replication lag von 45 s — automatisch behoben',
    status: 'resolved',
    icon: 'check',
    time: 'gestern',
    owner: 'Auto',
  },
];
